import React, { useEffect, useState } from 'react';
import { Lock, Unlock, Clock, Link as LinkIcon, Check, ClipboardList, Printer, Plus, Edit2, Trash2, X, Save, RotateCcw, Users, Store as StoreIcon } from 'lucide-react';
import { Store, Order, StoreOrderType } from '../../types';
import { getStores, updateStore, createStore, deleteStore, getDeletedStores, restoreStore } from '../../api/stores';
import { getAllOrders } from '../../api/orders';
import {
  STORE_DAY_LABELS,
  STORE_HOUR_OPTIONS,
  STORE_OPERATION_DAY_OPTIONS,
  formatStoreOperationSummary,
  toggleOperationDay,
} from '../../utils/storeOperation';

interface StoresTabProps {
  onUpdateStore?: (store: Store) => void;
  stores: Store[];
  orders: Order[];
  selectedOrderSheetId: string;
  onViewOrders: (storeId: string) => void;
  onPrint?: (storeId: string) => void;
  isAdmin?: boolean;
  sellers?: { id: string; username: string }[];
}

export const MasterStoresTab: React.FC<StoresTabProps> = ({
  onUpdateStore: parentUpdateStore,
  stores: initialStores,
  orders: initialOrders,
  selectedOrderSheetId,
  onViewOrders,
  onPrint,
  isAdmin = false,
  sellers = []
}) => {
  const defaultOperationDays = [1, 2, 3, 4, 5];
  const [orders, setOrders] = useState<Order[]>(initialOrders || []);
  const [stores, setStores] = useState<Store[]>(initialStores || []);
  const [deletedStores, setDeletedStores] = useState<Store[]>([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [copiedStoreId, setCopiedStoreId] = useState<string | null>(null);

  // Creation state
  const [isAdding, setIsAdding] = useState(false);
  const [newStoreName, setNewStoreName] = useState('');
  const [newStoreSlug, setNewStoreSlug] = useState('');
  const [newStoreType, setNewStoreType] = useState<StoreOrderType>('DELIVERY');
  const [newStoreDay, setNewStoreDay] = useState<number>(1);
  const [newStoreOperationDayOfWeeks, setNewStoreOperationDayOfWeeks] =
    useState<number[]>(defaultOperationDays);
  const [newStoreOperationStartHour, setNewStoreOperationStartHour] =
    useState<number>(9);
  const [newStoreOperationEndHour, setNewStoreOperationEndHour] =
    useState<number>(18);
  const [newStoreSellerId, setNewStoreSellerId] = useState<string>('');

  // Editing state
  const [editingStoreId, setEditingStoreId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [editType, setEditType] = useState<StoreOrderType>('DELIVERY');
  const [editDay, setEditDay] = useState<number>(1);
  const [editOperationDayOfWeeks, setEditOperationDayOfWeeks] = useState<
    number[]
  >(defaultOperationDays);
  const [editOperationStartHour, setEditOperationStartHour] =
    useState<number>(9);
  const [editOperationEndHour, setEditOperationEndHour] = useState<number>(18);
  const [memoEdits, setMemoEdits] = useState<Record<string, string>>({});

  // Seller filtering
  const [selectedSellerId, setSelectedSellerId] = useState<string>('all');

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      } else {
        // Fallback for non-secure contexts
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      }
    } catch (err) {
      console.error('Copy failed', err);
      return false;
    }
  };

  const buildStoreLink = (store: Store) =>
    `${window.location.origin}/store/${store.slug || store.id}`;

  const getStoreErrorMessage = (error: any, fallback: string) => {
    const message = error?.response?.data?.message;
    if (Array.isArray(message)) {
      return message.join('\n');
    }

    return message || fallback;
  };

  const refreshData = () => {
    getStores().then(setStores).catch(console.error);
    getDeletedStores().then(setDeletedStores).catch(console.error);
    getAllOrders().then(setOrders).catch(console.error);
  };

  useEffect(() => {
    refreshData();
  }, []);

  const handleCreateStore = async () => {
    if (!newStoreName.trim()) return;
    try {
      await createStore(newStoreName.trim(), {
        slug: newStoreSlug.trim() || undefined,
        orderType: newStoreType,
        defaultDeliveryDay:
          newStoreType === 'DELIVERY' ? newStoreDay : undefined,
        operationDayOfWeeks:
          newStoreType === 'STORE' ? newStoreOperationDayOfWeeks : undefined,
        operationStartHour:
          newStoreType === 'STORE' ? newStoreOperationStartHour : undefined,
        operationEndHour:
          newStoreType === 'STORE' ? newStoreOperationEndHour : undefined,
        sellerId: isAdmin ? newStoreSellerId : undefined
      });
      setNewStoreName('');
      setNewStoreSlug('');
      setNewStoreType('DELIVERY');
      setNewStoreDay(1);
      setNewStoreOperationDayOfWeeks(defaultOperationDays);
      setNewStoreOperationStartHour(9);
      setNewStoreOperationEndHour(18);
      setNewStoreSellerId(selectedSellerId !== 'all' ? selectedSellerId : '');
      setIsAdding(false);
      refreshData();
      alert('거점이 추가되었습니다.');
    } catch (e) {
      console.error(e);
      alert(getStoreErrorMessage(e, '거점 추가 실패'));
    }
  };

  const handleUpdateStore = async (s: Store) => {
    try {
      await updateStore(s.id, {
        name: editName.trim(),
        slug: editSlug.trim() || null,
        orderType: editType,
        defaultDeliveryDay: editType === 'DELIVERY' ? editDay : undefined,
        operationDayOfWeeks:
          editType === 'STORE' ? editOperationDayOfWeeks : [],
        operationStartHour:
          editType === 'STORE' ? editOperationStartHour : undefined,
        operationEndHour:
          editType === 'STORE' ? editOperationEndHour : undefined,
      });
      setEditingStoreId(null);
      refreshData();
    } catch (e) {
      console.error(e);
      alert(getStoreErrorMessage(e, '수정 실패'));
    }
  };

  const handleDeleteStore = async (id: string) => {
    if (!confirm('정말 이 거점을 삭제하시겠습니까? \n삭제된 거점은 [삭제된 거점 보기]에서 복구할 수 있습니다.')) return;
    try {
      await deleteStore(id);
      refreshData();
    } catch (e) {
      console.error(e);
      alert('삭제 실패');
    }
  };

  const handleRestoreStore = async (id: string) => {
    if (!confirm('이 거점을 복구하시겠습니까?')) return;
    try {
      await restoreStore(id);
      refreshData();
      alert('거점이 복구되었습니다.');
    } catch (e) {
      console.error(e);
      alert('복구 실패');
    }
  };

  const startEditing = (s: Store) => {
    setEditingStoreId(s.id);
    setEditName(s.name);
    setEditSlug(s.slug || '');
    setEditType(s.orderType || 'DELIVERY');
    setEditDay(s.defaultDeliveryDay ?? 1);
    setEditOperationDayOfWeeks(s.operationDayOfWeeks || defaultOperationDays);
    setEditOperationStartHour(s.operationStartHour ?? 9);
    setEditOperationEndHour(s.operationEndHour ?? 18);
  };

  const getOrderTypeLabel = (orderType?: StoreOrderType) =>
    orderType === 'STORE' ? '매장형' : '배송지형';

  const renderStore = (s: Store) => {
    const isEditing = editingStoreId === s.id;
    const storeOrders = orders.filter(o => o.storeId === s.id);
    const storeRev = storeOrders.reduce((acc, o) => acc + o.totalPrice, 0);

    return (
      <div key={s.id} className={`bg-white p-5 rounded-3xl border shadow-sm space-y-4 transition-all ${isEditing ? 'ring-2 ring-emerald-500 border-transparent shadow-xl' : ''}`}>
        {isEditing ? (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-[10px] font-black text-emerald-500 uppercase tracking-wider">거점 수정</span>
              <div className="flex gap-1">
                <button onClick={() => setEditingStoreId(null)} className="p-1.5 text-gray-400 hover:text-gray-600"><X size={16} /></button>
                <button onClick={() => handleUpdateStore(s)} className="p-1.5 text-emerald-500 hover:text-emerald-600"><Save size={16} /></button>
              </div>
            </div>
            <div className="w-full h-10 px-3 bg-gray-100 border rounded-xl text-xs font-bold flex items-center text-gray-400 cursor-not-allowed">
              {s.name}
            </div>
            <div className="flex overflow-hidden rounded-xl border border-emerald-200 bg-white">
              <span className="flex items-center px-3 text-[10px] font-bold text-gray-400 border-r border-gray-100">
                /store/
              </span>
              <input
                value={editSlug}
                onChange={e => setEditSlug(e.target.value)}
                placeholder="gangnam"
                className="min-w-0 flex-1 h-10 px-3 bg-transparent text-xs font-bold outline-none"
              />
            </div>
            <select
              value={editType}
              onChange={e => setEditType(e.target.value as StoreOrderType)}
              className="w-full h-10 px-4 bg-white border border-emerald-200 rounded-xl text-xs font-bold outline-none"
            >
              <option value="DELIVERY">배송지형 주문</option>
              <option value="STORE">매장형 주문</option>
            </select>
            {editType === 'DELIVERY' ? (
              <select
                value={editDay}
                onChange={e => setEditDay(Number(e.target.value))}
                className="w-full h-10 px-4 bg-white border border-emerald-200 rounded-xl text-xs font-bold outline-none"
              >
                {STORE_DAY_LABELS.map((day, idx) => (
                  <option key={idx} value={idx}>{day}요일</option>
                ))}
              </select>
            ) : (
              <>
                <div className="grid grid-cols-4 gap-2">
                  {STORE_OPERATION_DAY_OPTIONS.map((day) => {
                    const isSelected = editOperationDayOfWeeks.includes(day.value);
                    return (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() =>
                          setEditOperationDayOfWeeks((prev) =>
                            toggleOperationDay(prev, day.value),
                          )
                        }
                        className={`h-10 rounded-2xl text-xs font-black border transition-all ${
                          isSelected
                            ? 'bg-emerald-500 text-white border-emerald-500'
                            : 'bg-gray-50 text-gray-500 border-gray-200'
                        }`}
                      >
                        {day.label}
                      </button>
                    );
                  })}
                </div>
                <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                  <select
                    value={editOperationStartHour}
                    onChange={e => {
                      const nextStartHour = Number(e.target.value);
                      setEditOperationStartHour(nextStartHour);
                      if (editOperationEndHour <= nextStartHour) {
                        setEditOperationEndHour(Math.min(24, nextStartHour + 1));
                      }
                    }}
                    className="w-full h-10 px-3 bg-white border border-emerald-200 rounded-xl text-xs font-bold outline-none"
                  >
                    {STORE_HOUR_OPTIONS.filter((option) => option.value < 24).map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <span className="text-xs font-black text-gray-400">~</span>
                  <select
                    value={editOperationEndHour}
                    onChange={e => setEditOperationEndHour(Number(e.target.value))}
                    className="w-full h-10 px-3 bg-white border border-emerald-200 rounded-xl text-xs font-bold outline-none"
                  >
                    {STORE_HOUR_OPTIONS.filter((option) => option.value > editOperationStartHour).map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h3 className="font-black text-gray-900">{s.name}</h3>
                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-md ${s.orderType === 'STORE' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>
                  {getOrderTypeLabel(s.orderType)}
                </span>
              </div>
              <button
                onClick={() => startEditing(s)}
                className="text-[10px] font-black text-gray-500 hover:text-gray-900 transition-colors"
              >
                {formatStoreOperationSummary(s)}
              </button>
              <button
                onClick={async () => {
                  const url = buildStoreLink(s);
                  const success = await copyToClipboard(url);
                  if (success) {
                    setCopiedStoreId(s.id);
                    setTimeout(() => setCopiedStoreId(null), 2000);
                  } else {
                    alert('링크 복사에 실패했습니다. 브라우저 설정을 확인해주세요.');
                  }
                }}
                className={`px-2 py-1.5 rounded-xl bg-gray-50 transition-all flex items-center gap-1.5 border border-gray-100 hover:bg-gray-100 ${copiedStoreId === s.id ? 'text-emerald-600 border-emerald-100 bg-emerald-50' : 'text-gray-600'}`}
              >
                <LinkIcon size={12} className={copiedStoreId === s.id ? 'text-emerald-500' : 'text-gray-400'} />
                <span className="text-[10px] font-black">{copiedStoreId === s.id ? '복사됨' : '매장 링크 복사'}</span>
              </button>
            </div>
            <div className="flex items-center gap-1">
              {showDeleted ? (
                <button
                  onClick={() => handleRestoreStore(s.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-full text-[10px] font-black hover:bg-emerald-100 transition-all"
                >
                  <RotateCcw size={12} />
                  복구하기
                </button>
              ) : (
                <>
                  {isAdmin && (
                    <>
                      <button onClick={() => startEditing(s)} className="p-1.5 text-gray-300 hover:text-gray-600 transition-colors"><Edit2 size={12} /></button>
                      <button onClick={() => handleDeleteStore(s.id)} className="p-1.5 text-gray-300 hover:text-red-500 transition-colors"><Trash2 size={12} /></button>
                    </>
                  )}
                  <button
                    onClick={async () => {
                      if (!isAdmin && !s.isClosed) {
                        alert("배송 마감/중지는 관리자만 변경할 수 있습니다.");
                        return;
                      }
                      try {
                        const nextStatus = !s.isClosed;
                        await updateStore(s.id, { isClosed: nextStatus });
                        refreshData();
                      } catch (error) {
                        console.error("Failed to toggle store status", error);
                        alert("상태 변경에 실패했습니다.");
                      }
                    }}
                    className={`ml-1 px-3 py-1.5 rounded-full text-[10px] font-black flex items-center gap-1 transition-all ${s.isClosed ? 'bg-red-50 text-red-500 hover:bg-red-100' : 'bg-emerald-50 text-emerald-500 hover:bg-emerald-100'}`}
                  >
                    {s.isClosed ? (
                      <>
                        <Lock size={12} />
                        <span>영업 마감</span>
                      </>
                    ) : (
                      <>
                        <Unlock size={12} />
                        <span>영업 가능</span>
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {!isEditing && !showDeleted && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black text-gray-400">주문 현황</span>
              <div className="flex gap-1">
                <button onClick={() => onViewOrders(s.id)} className="p-2 bg-gray-50 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors"><ClipboardList size={14} /></button>
                <button onClick={() => onPrint?.(s.id)} className="p-2 bg-emerald-50 rounded-xl text-emerald-600 hover:bg-emerald-100 transition-colors"><Printer size={14} /></button>
              </div>
            </div>

            <div className="bg-gray-50/50 rounded-2xl p-3 flex justify-between items-center">
              <div className="text-[10px] font-black text-gray-400">총 {storeOrders.length}건</div>
              <div className="text-xs font-black text-gray-900">{storeRev.toLocaleString()}원</div>
            </div>

            <div className="flex items-center gap-2 bg-orange-50/50 p-2.5 rounded-xl border border-orange-100/50">
              <Clock size={12} className="text-orange-400" />
              <span className="text-[10px] font-bold text-orange-600/70">운영 메모</span>
              <input
                className="flex-1 bg-transparent text-[10px] font-black outline-none text-orange-900 placeholder:text-orange-200"
                placeholder="운영 메모를 입력하세요"
                value={memoEdits[s.id] !== undefined ? memoEdits[s.id] : (s.deliverySchedule || '')}
                onChange={(e) => {
                  setMemoEdits(prev => ({ ...prev, [s.id]: e.target.value }));
                }}
              />
              {(memoEdits[s.id] !== undefined && memoEdits[s.id] !== (s.deliverySchedule || '')) && (
                <button
                  onClick={async () => {
                    try {
                      const newMemo = memoEdits[s.id];
                      const updated = { ...s, deliverySchedule: newMemo };
                      if (parentUpdateStore) await parentUpdateStore(updated);
                      // Clear local edit state for this store
                      setMemoEdits(prev => {
                        const next = { ...prev };
                        delete next[s.id];
                        return next;
                      });
                      refreshData();
                    } catch (error) {
                      console.error("Failed to update memo", error);
                    }
                  }}
                  className="p-1.5 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition-colors"
                >
                  <Save size={14} />
                </button>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4 pb-20">
      <div className="flex justify-between items-center px-1">
        <h2 className="text-lg font-black text-gray-900">거점 관리</h2>
        {isAdmin && (
          <button
            onClick={() => setShowDeleted(!showDeleted)}
            className={`text-[10px] font-black px-3 py-1.5 rounded-xl transition-all ${showDeleted ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-100' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
          >
            {showDeleted ? '활성 거점 보기' : '삭제된 거점 보기'}
          </button>
        )}
      </div>

      {/* Seller Filter */}
      {isAdmin && sellers.length > 0 && (
        <div className="px-1 flex items-center gap-2">
          <div className="flex-1 relative">
            <Users size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <select
              value={selectedSellerId}
              onChange={(e) => {
                setSelectedSellerId(e.target.value);
                setNewStoreSellerId(e.target.value !== 'all' ? e.target.value : '');
              }}
              className="w-full h-10 pl-9 pr-4 bg-white border border-gray-200 rounded-2xl text-[11px] font-black outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all appearance-none"
            >
              <option value="all">전체 판매자 보기</option>
              {sellers.map(s => (
                <option key={s.id} value={s.id}>{s.username}의 거점</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Add Store Section */}
      {isAdmin && !showDeleted && !isAdding && (
        <button
          onClick={() => {
            setIsAdding(true);
            setNewStoreSellerId(selectedSellerId !== 'all' ? selectedSellerId : '');
          }}
          className="mx-1 py-4 bg-white border-2 border-dashed border-gray-200 rounded-3xl text-gray-400 font-black text-xs flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
        >
          <Plus size={16} />
          {selectedSellerId !== 'all'
            ? `${sellers.find(s => s.id === selectedSellerId)?.username || ''} 거점 추가`
            : '새 거점 추가'}
        </button>
      )}

      {isAdmin && !showDeleted && isAdding && (
        <div className="bg-white p-6 rounded-3xl border shadow-md space-y-4 animate-in fade-in slide-in-from-top-2">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-black text-gray-900">새 거점 정보</h3>
            <button onClick={() => setIsAdding(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">거점명</label>
              <input
                value={newStoreName}
                onChange={e => setNewStoreName(e.target.value)}
                placeholder="예: 강남점, 역삼 오피스"
                className="w-full h-11 px-4 bg-gray-50 border rounded-2xl text-xs font-bold outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">짧은 링크</label>
              <div className="flex overflow-hidden rounded-2xl border bg-gray-50 focus-within:ring-2 ring-emerald-500/20 focus-within:border-emerald-500 transition-all">
                <span className="flex items-center px-4 text-[11px] font-bold text-gray-400 border-r border-gray-100">
                  /store/
                </span>
                <input
                  value={newStoreSlug}
                  onChange={e => setNewStoreSlug(e.target.value)}
                  placeholder="gangnam"
                  className="min-w-0 flex-1 h-11 px-4 bg-transparent text-xs font-bold outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">거점 유형</label>
              <select
                value={newStoreType}
                onChange={e => setNewStoreType(e.target.value as StoreOrderType)}
                className="w-full h-11 px-4 bg-gray-50 border rounded-2xl text-xs font-bold outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all"
              >
                <option value="DELIVERY">배송지형 주문</option>
                <option value="STORE">매장형 주문</option>
              </select>
            </div>
            {newStoreType === 'DELIVERY' ? (
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">기본 배송 요일</label>
                <select
                  value={newStoreDay}
                  onChange={e => setNewStoreDay(Number(e.target.value))}
                  className="w-full h-11 px-4 bg-gray-50 border rounded-2xl text-xs font-bold outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all"
                >
                  {STORE_DAY_LABELS.map((day, idx) => (
                    <option key={idx} value={idx}>{day}요일</option>
                  ))}
                </select>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">운영일자</label>
                  <div className="grid grid-cols-4 gap-2">
                    {STORE_OPERATION_DAY_OPTIONS.map((day) => {
                      const isSelected = newStoreOperationDayOfWeeks.includes(day.value);
                      return (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() =>
                            setNewStoreOperationDayOfWeeks((prev) =>
                              toggleOperationDay(prev, day.value),
                            )
                          }
                          className={`h-10 rounded-2xl text-xs font-black border transition-all ${
                            isSelected
                              ? 'bg-emerald-500 text-white border-emerald-500'
                              : 'bg-gray-50 text-gray-500 border-gray-200'
                          }`}
                        >
                          {day.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">운영시간</label>
                  <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                    <select
                      value={newStoreOperationStartHour}
                      onChange={e => {
                        const nextStartHour = Number(e.target.value);
                        setNewStoreOperationStartHour(nextStartHour);
                        if (newStoreOperationEndHour <= nextStartHour) {
                          setNewStoreOperationEndHour(Math.min(24, nextStartHour + 1));
                        }
                      }}
                      className="w-full h-11 px-4 bg-gray-50 border rounded-2xl text-xs font-bold outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all"
                    >
                      {STORE_HOUR_OPTIONS.filter((option) => option.value < 24).map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <span className="text-xs font-black text-gray-400">~</span>
                    <select
                      value={newStoreOperationEndHour}
                      onChange={e => setNewStoreOperationEndHour(Number(e.target.value))}
                      className="w-full h-11 px-4 bg-gray-50 border rounded-2xl text-xs font-bold outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all"
                    >
                      {STORE_HOUR_OPTIONS.filter((option) => option.value > newStoreOperationStartHour).map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}
            {isAdmin && sellers.length > 0 && (
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">거점 담당 판매자</label>
                <select
                  value={newStoreSellerId}
                  onChange={e => setNewStoreSellerId(e.target.value)}
                  className="w-full h-11 px-4 bg-gray-50 border rounded-2xl text-xs font-bold outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all appearance-none"
                >
                  <option value="">판매자 선택 안함 (본인 할당)</option>
                  {sellers.map(s => (
                    <option key={s.id} value={s.id}>{s.username}</option>
                  ))}
                </select>
              </div>
            )}
            <button
              onClick={handleCreateStore}
              className="w-full h-12 bg-emerald-500 text-white rounded-2xl font-black text-xs shadow-lg shadow-emerald-100 hover:bg-emerald-600 transition-colors"
            >
              거점 등록하기
            </button>
          </div>
        </div>
      )}

      {(() => {
        const displayedStores = showDeleted ? deletedStores : stores || [];
        const filteredStores = selectedSellerId === 'all'
          ? displayedStores
          : displayedStores.filter(s => s.sellerId === selectedSellerId);

        if (filteredStores.length === 0) {
          return (
            <div className="py-20 text-center space-y-2">
              <div className="text-gray-300 flex justify-center"><StoreIcon size={48} /></div>
              <p className="text-sm font-black text-gray-400">등록된 거점이 없습니다.</p>
            </div>
          );
        }

        // Only group by seller if "all" is selected and we are not in deleted view
        if (selectedSellerId === 'all' && !showDeleted) {
          const grouped: Record<string, Store[]> = {};
          filteredStores.forEach(s => {
            const sId = s.sellerId || 'unassigned';
            if (!grouped[sId]) grouped[sId] = [];
            grouped[sId].push(s);
          });

          return Object.entries(grouped).map(([sellerId, sellerStores]) => {
            const seller = sellers.find(sel => sel.id === sellerId);
            return (
              <div key={sellerId} className="space-y-3">
                <div className="px-2 flex items-center gap-2">
                  <div className="h-px flex-1 bg-gray-100" />
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                    <Users size={12} />
                    {seller ? seller.username : '관리자/미할당'} ({sellerStores.length})
                  </span>
                  <div className="h-px flex-1 bg-gray-100" />
                </div>
                {sellerStores.map(s => renderStore(s))}
              </div>
            );
          });
        }

        return filteredStores.map(s => renderStore(s));
      })()}

    </div>
  );
};
